import { useState, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import ChatArea from './ChatArea';
import Composer from './Composer';
import { 
  MessageSquare,
  Plus,
  Sparkles,
  History,
  Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';

export default function AIChat() {
  const { 
    threads, 
    currentThread, 
    selectThread, 
    createThread,
    sendMessage,
    selectedBrand 
  } = useChat();
  const [showHistory, setShowHistory] = useState(false);

  const handleNewChat = async () => {
    if (selectedBrand) {
      await createThread(selectedBrand.brand_id);
    }
  };
  
  const handlePromptClick = async (prompt) => {
    if (selectedBrand) {
      const result = await createThread(selectedBrand.brand_id, prompt);
      // Send the initial message after thread is created
      if (result && result.thread && result.initialMessage) {
        // Pass the thread directly to avoid state timing issues
        await sendMessage(result.initialMessage, result.thread);
      }
    }
  };

  return (
    <div className="h-full flex" data-testid="ai-chat">
      {/* Thread History Sidebar */}
      <div className={cn(
        "h-full bg-card border-r border-border flex flex-col transition-all duration-300",
        showHistory ? "w-72" : "w-0 overflow-hidden"
      )}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <History className="w-4 h-4" />
              Chat History
            </h3>
            <Button 
              size="sm" 
              onClick={handleNewChat}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              New
            </Button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {threads.map((thread) => (
            <button
              key={thread.thread_id}
              onClick={() => selectThread(thread)}
              className={cn(
                "w-full p-3 rounded-lg text-left transition-all",
                currentThread?.thread_id === thread.thread_id
                  ? "bg-primary/10 border border-primary/20"
                  : "hover:bg-muted"
              )}
            >
              <p className="text-sm font-medium truncate">{thread.title || 'New Chat'}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(thread.updated_at).toLocaleDateString()}
              </p>
            </button>
          ))}
          
          {threads.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No conversations yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full">
        {/* Chat Header */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-border bg-card/50">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold">Fundle Brain AI</p>
                <p className="text-xs text-muted-foreground">Ask anything about your data</p>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleNewChat} className="gap-2">
            <Plus className="w-4 h-4" />
            New Chat
          </Button>
        </div>

        {/* Welcome State or Chat */}
        {!currentThread ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-2xl text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-3">AI Conversation Analytics</h2>
              <p className="text-muted-foreground mb-8">
                Chat with your loyalty data using natural language. Ask questions, 
                get insights, and discover patterns in your customer behavior.
              </p>
              
              {/* Suggested Prompts */}
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "What's my total revenue this year?",
                  "Show me my top 10 performing stores",
                  "How many repeat customers do I have?",
                  "What's my RFM segmentation?"
                ].map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePromptClick(prompt)}
                    className="p-4 rounded-xl bg-muted/50 hover:bg-muted text-left transition-all border border-transparent hover:border-primary/20"
                  >
                    <p className="text-sm">{prompt}</p>
                  </button>
                ))}
              </div>
              
              <Button 
                onClick={handleNewChat} 
                size="lg" 
                className="mt-8 gap-2"
              >
                <Plus className="w-5 h-5" />
                Start New Conversation
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ChatArea />
            <Composer />
          </>
        )}
      </div>
    </div>
  );
}
